import type { ICellRendererParams } from 'ag-grid-enterprise';
import type Big from 'big.js';
import { get, isFunction } from 'lodash-es';
import type { PropsWithChildren, ReactNode } from 'react';
import { Order } from '../../types/Order';
import { type ExpressedOutcomeEnum, OrdTypeEnum, PricingModeEnum, SideEnum } from '../../types/types';
import {
  getPricingReference,
  messages as prettyPricingMessages,
  prettyPricingMode,
  toBigWithDefault,
} from '../../utils';
import { formatUSTreasuryPriceWithRounding } from '../../utils/usTreasuryPrice';
import type { PriceValue, PriceValueConfirm } from '../BlotterTable/aggFuncs/priceAggFunc';
import type { BlotterTableAgGridContext } from '../BlotterTable/BlotterTableContext';
import { ConfirmPrice } from '../Confirm/ConfirmPrice';
import { Box, Flex } from '../Core';
import { InlineFormattedNumber } from '../FormattedNumber/InlineFormattedNumber';
import { useInlineFormattedNumberContext } from '../FormattedNumber/InlineFormattedNumberContext';
import { NumberVariants } from '../FormattedNumber/types';
import { IconName } from '../Icons';
import { IndicatorBadge } from '../IndicatorBadge';
import { FormattedMessage } from '../Intl';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';
import type { AgGridPriceProps } from './AgGridPrice.types';

function isPrettyPricingMessageKey(key: string): key is keyof typeof prettyPricingMessages {
  return key in prettyPricingMessages;
}

export function AgGridPrice({
  value,
  data,
  context,
  showCurrency = true,
  round,
  increment: incrementInput,
  showFeeIcon = 'auto',
  colDef,
  side: inputSide,
  getSide,
  showReferencePrice,
  showDashIfZero,
  expressedOutcomeField,
  align,
}: AgGridPriceProps &
  Pick<ICellRendererParams<unknown, PriceValue>, 'colDef' | 'value' | 'data'> & {
    context: BlotterTableAgGridContext;
  }) {
  const { forceAlign } = useInlineFormattedNumberContext();
  const securityInfo = value?.asset ? context.current.securitiesBySymbol?.get(value.asset) : undefined;
  const currencyInfo = value?.quoteCurrency ? context.current.currenciesBySymbol?.get(value.quoteCurrency) : undefined;
  const ordType = data instanceof Order ? data.OrdType : undefined;
  const expressedOutcome: ExpressedOutcomeEnum | undefined = expressedOutcomeField
    ? get(data, expressedOutcomeField)
    : undefined;

  let shouldShowFeeIcon = showFeeIcon === 'always';
  if (showFeeIcon === 'auto') {
    shouldShowFeeIcon = ordType === OrdTypeEnum.LimitAllIn || colDef?.field === 'AvgPxAllIn';
  }

  // If a sideField is provided, we try to derive the color (variant) from the resolved side
  const side: unknown = inputSide ?? getSide?.(data);
  const variant =
    side === SideEnum.Buy ? NumberVariants.Positive : side === SideEnum.Sell ? NumberVariants.Negative : undefined;

  const maybePricingMode = get(data, 'PricingMode');
  // Absolute references may represent a currency price or a percentage rate. Supporting them here requires
  // an explicit way to resolve the unit from product context; PricingMode alone cannot determine it safely.
  const maybePricingReference =
    maybePricingMode === PricingModeEnum.Absolute ? undefined : get(data, 'PricingReference');

  const increment = isFunction(incrementInput) ? incrementInput(data) : incrementInput;
  const shouldShowCurrency = isFunction(showCurrency) ? showCurrency(data) : showCurrency;
  const defaultIncrement = securityInfo?.DefaultPriceIncrement ?? currencyInfo?.DefaultIncrement;
  const quoteCurrency = currencyInfo?.Symbol ?? value?.quoteCurrency;
  const shouldShowPriceCurrency = shouldShowCurrency && !value?.shouldFormatAsUSTreasury;

  if (!value || !value.price) {
    if (showReferencePrice) {
      // If no price returned from market (bad connection or order not yet started), we show the reference price
      const amount = getPricingReference({ PricingMode: maybePricingMode, PricingReference: maybePricingReference });
      return (
        <InlineFormattedNumber number={amount?.value} currency={amount?.currency} round={round} variant={variant} />
      );
    }

    return <></>;
  }
  if (showDashIfZero && toBigWithDefault(value.price, 0).eq(0)) {
    return '-';
  }

  function renderPriceNumber({
    price,
    currency,
    confirm,
    startIcon,
  }: {
    price: Big;
    currency: string | null | undefined;
    confirm?: PriceValueConfirm;
    startIcon?: IconName;
  }) {
    if (value?.shouldFormatAsUSTreasury) {
      const formatted = formatUSTreasuryPriceWithRounding(price);
      return (
        <InlineFormattedNumber
          number={price}
          currency={currency}
          startIcon={startIcon}
          variant={variant}
          align={forceAlign ?? align ?? 'right'}
          presentation={formatted}
          pretty={false}
        />
      );
    }

    if (confirm) {
      return <ConfirmPrice confirm={confirm} startIcon={startIcon} variant={variant} round={round} />;
    }

    if (value?.isRate) {
      return (
        <InlineFormattedNumber
          number={price.times(100)}
          round={round}
          variant={variant}
          increment="0.01"
          currency="%"
          startIcon={startIcon}
        />
      );
    }

    return (
      <InlineFormattedNumber
        number={price}
        round={round}
        variant={variant}
        increment={increment ?? defaultIncrement}
        currency={currency}
        startIcon={startIcon}
      />
    );
  }

  return (
    <ReferencePriceTooltip
      disabled={!showReferencePrice}
      entity={{ PricingMode: maybePricingMode, PricingReference: maybePricingReference }}
    >
      <BestPriceBox isBestPrice={value.isBestPrice}>
        {renderPriceNumber({
          price: value.price,
          currency: shouldShowPriceCurrency && value.endPrice === undefined ? quoteCurrency : null,
          confirm: value.confirm,
          startIcon: shouldShowFeeIcon ? IconName.Fees : undefined,
        })}

        {value.endPrice && (
          <>
            &nbsp;-&nbsp;
            {renderPriceNumber({
              price: value.endPrice,
              currency: shouldShowPriceCurrency ? quoteCurrency : null,
              confirm: value.confirm,
            })}
          </>
        )}
      </BestPriceBox>

      {expressedOutcome && <ExpressedOutcomeIndicator expressedOutcome={expressedOutcome} />}
    </ReferencePriceTooltip>
  );
}

function BestPriceBox({ isBestPrice, children }: PropsWithChildren<{ isBestPrice: boolean }>) {
  if (!isBestPrice) {
    return children;
  }

  return (
    <Box p="spacing4" borderRadius="2px" background="colors.primary.dim" data-best-price>
      {children}
    </Box>
  );
}

function ExpressedOutcomeIndicator({ expressedOutcome }: { expressedOutcome: ExpressedOutcomeEnum }) {
  return (
    <IndicatorBadge variant={expressedOutcome === 'Yes' ? 'POSITIVE' : 'NEGATIVE'} ml="spacing4">
      {expressedOutcome === 'Yes' ? 'Y' : 'N'}
    </IndicatorBadge>
  );
}

/**
 * Renders the PricingReference as a tooltip.
 */
function ReferencePriceTooltip({
  entity,
  disabled = false,
  children,
}: {
  entity: Partial<Pick<Order, 'PricingReference' | 'PricingMode'>>;
  disabled?: boolean;
  children: ReactNode;
}) {
  if (!entity.PricingMode || !entity.PricingReference || disabled) {
    return <>{children}</>;
  }

  const amount = getPricingReference(entity);
  if (!amount) {
    return <>{children}</>;
  }

  const pricingMode = prettyPricingMode(entity.PricingMode);
  const renderedPricingMode = isPrettyPricingMessageKey(pricingMode) ? (
    <FormattedMessage {...prettyPricingMessages[pricingMode]} />
  ) : (
    pricingMode
  );

  return (
    <Tooltip
      usePortal
      showUnderline
      tooltip={
        <Flex gap="spacing4">
          <Text>{renderedPricingMode}:</Text>
          <InlineFormattedNumber number={amount.value} currency={amount.currency} />
        </Flex>
      }
    >
      {children}
    </Tooltip>
  );
}
